import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useJsonApi } from '@/modules/api/hooks/useJsonApi';
import { queryKeys, sortArrayQueryKey } from '@/modules/api/queryKeys';
import { JsonApiClient } from '@/modules/api/utils/createJsonApiClient';
import { GetDemoNegotiationParams } from '@/modules/demoNegotiation/resources/demoNegotiation/demoNegotiationResourceConsts';
import { constructMeta } from '@/modules/reactQuery/constructMeta';

import { getDemoNegotiationsMetricsResponseStruct } from './helpers/getDemoNegotiationsMetricsResponseStruct';
import { parseGetDemoNegotiationsMetricsResponse } from './helpers/parseGetDemoNegotiationsMetricsResponse';

export const useGetDemoNegotiationsMetricsQuery = (
  params: GetDemoNegotiationParams
) => {
  const { t } = useTranslation(
    'apiMessages/useGetDemoNegotiationsMetricsQuery'
  );
  const jsonApi = useJsonApi();

  return useQuery({
    queryKey: [
      queryKeys.customer.getDemoMetrics,
      sortArrayQueryKey(params.state),
    ],
    queryFn: getDemoNegotiationsMetrics(jsonApi, params),
    meta: constructMeta({
      errorMessages: {
        401: {
          UNAUTHENTICATED: t('Your session has expired. Please sign in'),
        },
        403: {
          FORBIDDEN: t("You don't have access to see metrics"),
        },
      },
    }),
  });
};

const getDemoNegotiationsMetrics = (
  jsonApi: JsonApiClient,
  params: GetDemoNegotiationParams
) => {
  return async () => {
    const response = await jsonApi.getOne({
      path: '/customer/demo_metrics',
      queryParams: getDemoMetricsQuery(params),
      responseStruct: getDemoNegotiationsMetricsResponseStruct,
    });

    return parseGetDemoNegotiationsMetricsResponse(response);
  };
};

const getDemoMetricsQuery = (params: GetDemoNegotiationParams) => {
  return {
    ...(params.state?.length && {
      'filter[state]': params.state.join(','),
    }),
  };
};
